import { Dialog } from '@base-ui/react/dialog'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useId, useMemo, useState } from 'react'
import { Avatar } from '@/components/media/media'
import { GlobeIcon, SearchIcon } from '@/components/ui/icons'
import { Badge, Button, QuotaBar, Spinner } from '@/components/ui/primitives'
import { useT } from '@/i18n/use-t'
import type { SearchSuggestion } from '@/lib/api/types'
import { cn } from '@/lib/cn'
import { useLocale, useSuggestions } from '@/lib/queries'
import { useAccountKey, useSession } from '@/lib/session'
import { addRecent, useRecent } from './recent'
import { filterByScope, groupSuggestions, type Scope } from './results'

export function useOpenSuggestion() {
  const navigate = useNavigate()
  const locale = useLocale()
  return (s: SearchSuggestion) => {
    if (s.kind === 'code' || s.kind === 'video')
      void navigate({ to: '/$locale/videos/$code', params: { locale, code: s.id } })
    else if (s.kind === 'actress')
      void navigate({ to: '/$locale/actresses/$id', params: { locale, id: s.id } })
    else void navigate({ to: '/$locale/discover', params: { locale }, search: { tag: s.id } })
  }
}

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const t = useT()
  const navigate = useNavigate()
  const locale = useLocale()
  const account = useAccountKey()
  const recent = useRecent(account)
  const { data: user } = useSession()
  const openSuggestion = useOpenSuggestion()
  const [q, setQ] = useState('')
  const [scope, setScope] = useState<Scope>('all')
  const [cursor, setCursor] = useState(0)
  const listId = useId()
  const suggestions = useSuggestions(q)
  const visible = useMemo(() => filterByScope(suggestions.data ?? [], scope), [suggestions.data, scope])
  const groups = groupSuggestions(visible)
  const ordered = [...groups.exact, ...groups.videos, ...groups.actresses, ...groups.tags]

  useEffect(() => {
    if (!open) {
      setQ('')
      setCursor(0)
    }
  }, [open])

  const choose = (s: SearchSuggestion) => {
    addRecent(account, q)
    onOpenChange(false)
    openSuggestion(s)
  }

  const goOnline = () => {
    addRecent(account, q)
    onOpenChange(false)
    void navigate({ to: '/$locale/search', params: { locale }, search: { q: q.trim(), scope: 'all' } })
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setCursor((c) => Math.min(ordered.length - 1, c + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setCursor((c) => Math.max(0, c - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (e.shiftKey) goOnline()
      else if (ordered[cursor]) choose(ordered[cursor])
      else if (q.trim().length >= 2) goOnline()
    } else if (e.key === 'Tab') {
      e.preventDefault()
      const scopes: Scope[] = ['all', 'videos', 'actresses', 'tags']
      setScope((s) => scopes[(scopes.indexOf(s) + (e.shiftKey ? 3 : 1)) % 4] as Scope)
      setCursor(0)
    }
  }

  const scopes: { value: Scope; label: string }[] = [
    { value: 'all', label: t('search.scopeAll') },
    { value: 'videos', label: t('search.scopeVideos') },
    { value: 'actresses', label: t('search.scopeActresses') },
    { value: 'tags', label: t('search.scopeTags') },
  ]

  let index = -1
  const row = (s: SearchSuggestion) => {
    index += 1
    const i = index
    const active = i === cursor
    return (
      // biome-ignore lint/a11y/useFocusableInteractive: focus stays on the combobox input via aria-activedescendant
      // biome-ignore lint/a11y/useKeyWithClickEvents: arrow keys and Enter are handled by the combobox input
      <div
        key={`${s.kind}-${s.id}`}
        role="option"
        id={`${listId}-${i}`}
        aria-selected={active}
        onMouseEnter={() => setCursor(i)}
        onClick={() => choose(s)}
        className={cn(
          'flex w-full cursor-pointer items-center gap-3.5 rounded-xl px-2.5 py-2 text-left',
          active ? 'bg-hover outline-2 outline-rose' : 'hover:bg-dusk',
        )}
      >
        {s.kind === 'actress' ? (
          <Avatar id={s.id} name={s.label} size={32} />
        ) : s.kind === 'tag' ? (
          <Badge tone="muted">#</Badge>
        ) : null}
        <span
          className={cn(
            'shrink-0',
            s.kind === 'code' || s.kind === 'video' ? 'w-28 font-mono text-[15px] text-rose' : 'text-sm',
          )}
        >
          {s.label}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm text-haze">{s.sub}</span>
        {s.kind === 'actress' && s.subscribed ? <Badge tone="solo">{t('actress.subscribed')}</Badge> : null}
        {active ? <span className="font-mono text-xs text-haze">{t('search.open')}</span> : null}
      </div>
    )
  }

  const section = (title: string, list: SearchSuggestion[]) =>
    list.length ? (
      <div className="flex flex-col gap-0.5 px-3 py-1">
        <div className="px-2.5 py-1.5 text-xs tracking-wide text-faint">{title}</div>
        <div role="group" aria-label={title} className="flex flex-col gap-0.5">
          {list.map(row)}
        </div>
      </div>
    ) : null

  const trimmed = q.trim()
  const quota = user?.quota.onlineSearch

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-50 bg-[rgba(6,5,10,0.78)] transition-opacity duration-150 data-[ending-style]:opacity-0 data-[starting-style]:opacity-0" />
        <Dialog.Popup
          aria-label={t('search.title')}
          className="fixed top-[8vh] left-1/2 z-50 flex max-h-[80vh] w-[min(760px,calc(100vw-32px))] -translate-x-1/2 flex-col overflow-hidden rounded-[22px] border border-line-strong bg-night outline-none transition-[opacity,transform] duration-150 data-[ending-style]:scale-[0.98] data-[ending-style]:opacity-0 data-[starting-style]:scale-[0.98] data-[starting-style]:opacity-0"
        >
          <Dialog.Title className="sr-only">{t('search.title')}</Dialog.Title>
          <div className="flex h-[68px] shrink-0 items-center gap-3.5 border-b border-line px-5">
            <SearchIcon size={20} className="text-rose" />
            <label htmlFor="palette-q" className="sr-only">
              {t('search.placeholder')}
            </label>
            <input
              id="palette-q"
              value={q}
              onChange={(e) => {
                setQ(e.target.value)
                setCursor(0)
              }}
              onKeyDown={onKeyDown}
              placeholder={t('search.placeholder')}
              role="combobox"
              aria-expanded
              aria-controls={listId}
              aria-activedescendant={ordered[cursor] ? `${listId}-${cursor}` : undefined}
              autoComplete="off"
              spellCheck={false}
              className="h-11 flex-1 bg-transparent font-mono text-xl text-moon outline-none placeholder:font-sans placeholder:text-base"
            />
            {suggestions.isFetching ? <Spinner className="text-faint" /> : null}
            <kbd className="rounded-md border border-line-strong px-2 py-0.5 font-mono text-xs text-haze">
              Esc
            </kbd>
          </div>
          <div
            className="flex shrink-0 gap-1.5 border-b border-line px-5 py-3"
            role="radiogroup"
            aria-label={t('search.title')}
          >
            {scopes.map((s) => (
              <button
                key={s.value}
                type="button"
                role="radio"
                aria-checked={scope === s.value}
                onClick={() => {
                  setScope(s.value)
                  setCursor(0)
                }}
                className={cn(
                  'h-8 rounded-lg px-3 text-[13px]',
                  scope === s.value ? 'bg-line text-moon' : 'text-haze hover:text-moon',
                )}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div
            id={listId}
            role="listbox"
            aria-label={t('search.title')}
            className="min-h-0 flex-1 overflow-y-auto py-2"
          >
            {trimmed.length < 2 ? (
              recent.length ? (
                <div className="flex flex-col gap-2 px-5 py-2">
                  <div className="text-xs text-faint">{t('search.recent')}</div>
                  <div className="flex flex-wrap gap-2">
                    {recent.map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setQ(r)}
                        className="h-8 rounded-full border border-line bg-deep px-3 font-mono text-[13px] text-moon hover:border-line-strong"
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="px-6 py-6 text-sm text-faint">{t('search.typeToSearch')}</p>
              )
            ) : ordered.length === 0 && !suggestions.isFetching ? (
              <p className="px-6 py-6 text-sm text-faint">{t('search.noResult')}</p>
            ) : (
              <>
                {section(t('search.exact'), groups.exact)}
                {section(t('search.videos'), groups.videos)}
                {section(t('search.actresses'), groups.actresses)}
                {section(t('search.tags'), groups.tags)}
              </>
            )}
          </div>
          {trimmed.length >= 2 ? (
            <div className="flex shrink-0 flex-wrap items-center gap-3.5 border-t border-line bg-deep px-5 py-4">
              <GlobeIcon size={18} className="text-cyan" />
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="text-sm">{t('search.noLocal')}</span>
                <span className="text-xs text-faint">{t('search.onlineHint')}</span>
              </div>
              {quota ? <QuotaBar used={quota.used} limit={quota.limit} /> : null}
              <Button size="sm" variant="signal" onClick={goOnline}>
                {t('search.onlineButton', { keyword: trimmed })} ⇧↵
              </Button>
            </div>
          ) : null}
          <div className="hidden shrink-0 gap-4 border-t border-line-soft px-5 py-2.5 font-mono text-[11px] text-faint md:flex">
            {t('search.keyboardHints')}
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
